import {
  AutocompleteItem,
  Avatar,
  Button,
  Checkbox,
  SelectItem,
} from "@heroui/react";
import AppSelect from "../../components/common/appSelect";
import AppAutocomplete from "../../components/common/appAutocomplete";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "../../utils/toast";
import {
  createMatchup,
  fetchTrainingDisciplines,
  fetchWeightClasses,
  getFighters,
  updateMatchup,
} from "../../services/api";
import {
  CreateMatchupRequest,
  DisplayMatchup,
  Matchup,
  MatchupResponse,
  Profile,
  UpdateMatchupRequest,
} from "../../types/types";


interface AddMatchupModalProps {
  isOpen: boolean;
  eventId: string;
  username?: string;
  nextBoutOrder: number;
  editingMatchup?: MatchupResponse | DisplayMatchup | Matchup | null;
  onClose: () => void;
  onCreated?: (matchup: Matchup) => void;
  onUpdated?: (matchup: Matchup) => void;
}

type MatchupFormErrors = {
  combatSport?: string;
  weightClass?: string;
  redCornerFighterId?: string;
  blueCornerFighterId?: string;
};

type MatchupSportPayload = {
  combatSport: string;
  weightClass: string;
};

type DealReference = {
  id?: string | number;
  dealId?: string | number;
};

type EditableMatchup = {
  id?: string | number;
  eventId?: string;
  boutOrder?: number;
  titleFight?: boolean;
  combatSport?: string;
  weightClass?: string;
  redCornerFighterId?: string | number;
  blueCornerFighterId?: string | number;
  redCornerFighterName?: string;
  blueCornerFighterName?: string;
  redCornerFighter?: Profile | string | null;
  blueCornerFighter?: Profile | string | null;
  redCornerDeal?: DealReference | null;
  blueCornerDeal?: DealReference | null;
};

type ApiError = {
  response?: {
    data?: {
      message?: string;
    };
  };
  message?: string;
};

type LoginResponse = {
  username?: string;
  email?: string;
};

type CornerPayload = {
  fighterId?: string;
  fighterName?: string;
};

const AddMatchupModal: React.FC<AddMatchupModalProps> = ({
  isOpen,
  eventId,
  username,
  nextBoutOrder,
  editingMatchup,
  onClose,
  onCreated,
  onUpdated,
}) => {
  const { t } = useTranslation("events");
  const navigate = useNavigate();

  const editableMatchup = editingMatchup as EditableMatchup | null | undefined;
  const isEditMode = Boolean(editingMatchup);

  const [boutOrder, setBoutOrder] = useState(nextBoutOrder || 1);
  const [titleFight, setTitleFight] = useState(false);
  const [combatSport, setCombatSport] = useState("");
  const [weightClass, setWeightClass] = useState("");
  const [redCornerFighterId, setRedCornerFighterId] = useState("");
  const [redCornerFighterName, setRedCornerFighterName] = useState("");
  const [blueCornerFighterId, setBlueCornerFighterId] = useState("");
  const [blueCornerFighterName, setBlueCornerFighterName] = useState("");
  const [sports, setSports] = useState<string[]>([]);
  const [weightClasses, setWeightClasses] = useState<string[]>([]);
  const [fighters, setFighters] = useState<Profile[]>([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [isLoadingFighters, setIsLoadingFighters] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<MatchupFormErrors>({});

  const hasDeal = (deal?: DealReference | null): boolean =>
    Boolean(deal?.id || deal?.dealId);

  const redCornerLocked = isEditMode && hasDeal(editableMatchup?.redCornerDeal);
  const blueCornerLocked = isEditMode && hasDeal(editableMatchup?.blueCornerDeal);

  const getFighterId = (fighter?: Profile | string | null): string => {
    if (!fighter) return "";
    if (typeof fighter === "string") return fighter;
    return String(fighter.id || "");
  };

  const getUsername = (): string => {
    try {
      const loginResponse = localStorage.getItem("loginResponse");
      if (!loginResponse) return "";

      const user: LoginResponse = JSON.parse(loginResponse);
      return user.username || user.email?.split("@")[0] || "";
    } catch {
      return "";
    }
  };

  const resetFighterSelection = () => {
    setRedCornerFighterId("");
    setRedCornerFighterName("");
    setBlueCornerFighterId("");
    setBlueCornerFighterName("");
  };

  const resetForm = () => {
    setBoutOrder(nextBoutOrder || 1);
    setTitleFight(false);
    setCombatSport("");
    setWeightClass("");
    resetFighterSelection();
    setErrors({});
    setFighters([]);
  };

  const clearError = (field: keyof MatchupFormErrors) => {
    setErrors((previousErrors) => ({
      ...previousErrors,
      [field]: undefined,
    }));
  };

  const formatOption = (value: string): string =>
    value
      .replaceAll("_", " ")
      .toLowerCase()
      .replace(/\b\w/g, (letter) => letter.toUpperCase());

  const fetchMatchupOptions = async () => {
    try {
      setIsLoadingOptions(true);

      const [sportData, weightData] = await Promise.all([
        fetchTrainingDisciplines(),
        fetchWeightClasses(),
      ]);

      setSports(Array.isArray(sportData) ? sportData : []);
      setWeightClasses(Array.isArray(weightData) ? weightData : []);
    } catch (error) {
      console.error("Failed to load matchup options:", error);
      toast.error(t("failed_to_load_matchup_options", "Failed to load matchup options"));
      setSports([]);
      setWeightClasses([]);
    } finally {
      setIsLoadingOptions(false);
    }
  };

  const fetchFighters = async () => {
    try {
      setIsLoadingFighters(true);

      const response = await getFighters({
        trainingDiscipline: combatSport || undefined,
        weightClass: weightClass || undefined,
        page: 0,
        pageSize: 100,
      });

      setFighters(Array.isArray(response?.data) ? response.data : []);
    } catch {
      toast.error(t("failed_to_load_fighters", "Failed to load fighters"));
      setFighters([]);
    } finally {
      setIsLoadingFighters(false);
    }
  };

  const getImageUrl = (image?: string | null): string | undefined => {
    if (!image) return undefined;

    const markdownMatch = image.match(/\]\((https?:\/\/[^)]+)\)/);
    if (markdownMatch?.[1]) return markdownMatch[1];

    const plainUrlMatch = image.match(/https?:\/\/[^\s)]+/);
    return plainUrlMatch?.[0] || image;
  };

  const getFullName = (fighter?: Profile): string => {
    if (!fighter) return t("fighter_not_selected", "Fighter not selected");

    const fullName = `${fighter.firstName || ""} ${fighter.lastName || ""}`.trim();
    return fullName || fighter.username || t("fighter_number", { id: fighter.id });
  };

  const getFighterSubtitle = (fighter: Profile): string => {
    const parts: string[] = [];

    if (fighter.username) parts.push(`@${fighter.username}`);

    const weight = fighter.fighter?.weightClasses?.[0];
    if (weight) parts.push(weight);

    return parts.join(" | ");
  };

  const getFighter = (id: string): Profile | undefined =>
    fighters.find((fighter) => String(fighter.id) === String(id));

  const getSelectedFighter = (
    id: string,
    fallbackFighter?: Profile | string | null
  ): Profile | undefined => {
    const selectedFighter = getFighter(id);
    if (selectedFighter) return selectedFighter;

    if (id && String(getFighterId(fallbackFighter)) === String(id)) {
      return fallbackFighter as Profile;
    }

    return undefined;
  };

  const getDiscipline = (fighter?: Profile): string =>
    fighter?.fighter?.trainingDisciplines?.[0] || t("not_available", "N/A");

  const getFighterWeightClass = (fighter?: Profile): string =>
    fighter?.fighter?.weightClasses?.[0] || t("not_available", "N/A");

  const getStance = (fighter?: Profile): string =>
    fighter?.fighter?.stance || t("not_available", "N/A");

  const getNationality = (fighter?: Profile): string =>
    fighter?.nationality || t("not_available", "N/A");

  const getNickname = (fighter?: Profile): string =>
    fighter?.fighter?.nickname
      ? `"${fighter.fighter.nickname}"`
      : t("no_nickname", "No nickname");

  const buildCornerPayload = (fighterId: string, fighterName: string): CornerPayload =>
    fighterId
      ? { fighterId, fighterName: undefined }
      : { fighterId: undefined, fighterName: fighterName.trim() || undefined };

  const validateForm = (): boolean => {
    const nextErrors: MatchupFormErrors = {};

    if (!combatSport) {
      nextErrors.combatSport = t("combat_sport_required", "Combat sport is required.");
    }

    if (!weightClass) {
      nextErrors.weightClass = t("weight_class_required", "Weight class is required.");
    }

    if (!redCornerFighterId && !redCornerFighterName.trim()) {
      nextErrors.redCornerFighterId = t(
        "red_corner_fighter_required",
        "Red corner fighter is required."
      );
    }

    if (!blueCornerFighterId && !blueCornerFighterName.trim()) {
      nextErrors.blueCornerFighterId = t(
        "blue_corner_fighter_required",
        "Blue corner fighter is required."
      );
    }

    const sameFighterSelected =
      redCornerFighterId &&
      blueCornerFighterId &&
      redCornerFighterId === blueCornerFighterId;

    if (sameFighterSelected) {
      nextErrors.blueCornerFighterId = t(
        "same_fighter_error",
        "Red and Blue corner fighters cannot be the same person"
      );
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const openFighterProfile = (fighterId: string | number) => {
    navigate(`/profile?profileId=${fighterId}`);
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setIsLoading(true);

      const redPayload = buildCornerPayload(redCornerFighterId, redCornerFighterName);
      const bluePayload = buildCornerPayload(blueCornerFighterId, blueCornerFighterName);

      if (isEditMode && editableMatchup?.id) {
        const updateRequest: UpdateMatchupRequest & MatchupSportPayload = {
          boutOrder: Number(boutOrder) || 1,
          titleFight,
          combatSport,
          weightClass,
          ...(!redCornerLocked && {
            redCornerFighterId: redPayload.fighterId,
            redCornerFighterName: redPayload.fighterName,
          }),
          ...(!blueCornerLocked && {
            blueCornerFighterId: bluePayload.fighterId,
            blueCornerFighterName: bluePayload.fighterName,
          }),
        };

        const response = await updateMatchup(
          username || getUsername(),
          Number(editableMatchup.id),
          updateRequest
        );

        const updatedMatchup: Matchup = {
          id: response.id,
          eventId: editableMatchup.eventId || eventId,
          boutOrder: response.boutOrder,
          titleFight: response.titleFight,
          redCornerFighter: response.redCornerFighter,
          blueCornerFighter: response.blueCornerFighter,
          redCornerFighterName: response.redCornerFighterName || undefined,
          blueCornerFighterName: response.blueCornerFighterName || undefined,
          status: response.status,
          opportunityCardId: response.opportunityCardId,
          resultType: response.resultType,
          createdAt: response.createdAt,
        };

        toast.success(t("matchup_updated_successfully", "Matchup updated successfully"));
        onUpdated?.(updatedMatchup);
      } else {
        const createRequest: CreateMatchupRequest & MatchupSportPayload = {
          boutOrder: Number(nextBoutOrder) || 1,
          titleFight,
          combatSport,
          weightClass,
          redCornerFighterId: redPayload.fighterId,
          redCornerFighterName: redPayload.fighterName,
          blueCornerFighterId: bluePayload.fighterId,
          blueCornerFighterName: bluePayload.fighterName,
        };

        const response = await createMatchup(
          username || getUsername(),
          eventId,
          createRequest
        );

        const newMatchup: Matchup = {
          id: response.id,
          eventId,
          boutOrder: response.boutOrder,
          titleFight: response.titleFight,
          redCornerFighter: response.redCornerFighter,
          blueCornerFighter: response.blueCornerFighter,
          redCornerFighterName: response.redCornerFighterName || undefined,
          blueCornerFighterName: response.blueCornerFighterName || undefined,
          status: response.status,
          opportunityCardId: response.opportunityCardId,
          resultType: response.resultType,
          createdAt: response.createdAt,
        };

        toast.success(t("matchup_created_successfully", "Matchup added successfully"));
        onCreated?.(newMatchup);
      }

      resetForm();
      onClose();
    } catch (error: unknown) {
      const apiError = error as ApiError;
      const errorMessage =
        apiError.response?.data?.message ||
        apiError.message ||
        (isEditMode
          ? t("failed_to_update_matchup", "Failed to update matchup")
          : t("failed_to_create_matchup", "Failed to add matchup"));

      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (isLoading) return;
    resetForm();
    onClose();
  };

  useEffect(() => {
    if (!isOpen) return;

    fetchMatchupOptions();

    if (!editableMatchup) {
      resetForm();
      return;
    }

    const initialRedId =
      String(editableMatchup.redCornerFighterId || "") ||
      getFighterId(editableMatchup.redCornerFighter);
    const initialBlueId =
      String(editableMatchup.blueCornerFighterId || "") ||
      getFighterId(editableMatchup.blueCornerFighter);

    setBoutOrder(editableMatchup.boutOrder || 1);
    setTitleFight(Boolean(editableMatchup.titleFight));
    setCombatSport(editableMatchup.combatSport || "");
    setWeightClass(editableMatchup.weightClass || "");
    setRedCornerFighterId(initialRedId);
    setRedCornerFighterName(
      initialRedId
        ? getFullName(editableMatchup.redCornerFighter as Profile)
        : editableMatchup.redCornerFighterName || ""
    );
    setBlueCornerFighterId(initialBlueId);
    setBlueCornerFighterName(
      initialBlueId
        ? getFullName(editableMatchup.blueCornerFighter as Profile)
        : editableMatchup.blueCornerFighterName || ""
    );
    setErrors({});
  }, [isOpen, editingMatchup, nextBoutOrder]);

  useEffect(() => {
    if (!isOpen) return;
    fetchFighters();
  }, [isOpen, combatSport, weightClass]);

  const redFighter = getSelectedFighter(
    redCornerFighterId,
    editableMatchup?.redCornerFighter
  );

  const blueFighter = getSelectedFighter(
    blueCornerFighterId,
    editableMatchup?.blueCornerFighter
  );

  const renderFighterCard = (fighter: Profile | undefined, corner: "red" | "blue") => {
    const isRed = corner === "red";
    const selectedId = isRed ? redCornerFighterId : blueCornerFighterId;
    const setSelectedId = isRed ? setRedCornerFighterId : setBlueCornerFighterId;
    const typedName = isRed ? redCornerFighterName : blueCornerFighterName;
    const setTypedName = isRed ? setRedCornerFighterName : setBlueCornerFighterName;
    const isLocked = isRed ? redCornerLocked : blueCornerLocked;
    const fieldName: keyof MatchupFormErrors = isRed
      ? "redCornerFighterId"
      : "blueCornerFighterId";
    const fieldError = errors[fieldName];
    const isManualEntry = !selectedId && typedName.trim().length > 0;
    const oppositeFighterId = isRed ? blueCornerFighterId : redCornerFighterId;
    const availableFighters = fighters.filter(
      (item) => String(item.id) !== String(oppositeFighterId)
    );

    const handleClear = () => {
      setSelectedId("");
      setTypedName("");
      clearError(fieldName);
    };

    const handleInputChange = (value: string) => {
      setTypedName(value);

      const matched = availableFighters.find(
        (item) => getFullName(item).toLowerCase() === value.trim().toLowerCase()
      );

      setSelectedId(matched ? String(matched.id) : "");
      clearError(fieldName);
    };

    const handleSelectionChange = (key: React.Key | null) => {
      if (!key) return;

      const matched = getFighter(String(key));

      setSelectedId(String(key));
      setTypedName(matched ? getFullName(matched) : String(key));
      clearError(fieldName);
    };

    return (
      <div
        className={`rounded-xl border ${
          isRed
            ? "border-red-200 bg-red-50/40"
            : "border-blue-200 bg-blue-50/40"
        } overflow-hidden`}
      >
        <div
          className={`px-4 py-2.5 flex items-center justify-between gap-2 ${
            isRed
              ? "bg-red-50"
              : "bg-blue-50"
          }`}
        >
          <div className="flex min-w-0 items-center gap-2">
            <span
              className={`w-2.5 h-2.5 rounded-full ${
                isRed
                  ? "bg-red-500"
                  : "bg-blue-500"
              }`}
            />

            <span className="truncate text-sm font-semibold text-gray-700 font-['Plus_Jakarta_Sans',sans-serif]">
              {isRed
                ? t("red_corner", "Red Corner")
                : t("blue_corner", "Blue Corner")}
            </span>
          </div>

          {typedName &&
            !isLocked && (
              <button
                type="button"
                onClick={handleClear}
                className={`text-[11px] font-medium ${
                  isRed
                    ? "text-red-400 hover:text-red-600"
                    : "text-blue-400 hover:text-blue-600"
                }`}
              >
                {t("clear", "Clear")}
              </button>
            )}
        </div>

        <div className="p-3">
          {isLocked ? (
            <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] font-medium text-amber-700">
              {t(
                "fighter_has_deal_locked",
                "This fighter has a deal and cannot be changed."
              )}
            </div>
          ) : (
            <AppAutocomplete
              aria-label={isRed ? "Red Corner" : "Blue Corner"}
              allowsCustomValue
              placeholder={
                isLoadingFighters
                  ? t("loading_fighters", "Loading fighters...")
                  : t("select_or_type_fighter", "Select or type a fighter name")
              }
              inputValue={typedName}
              selectedKey={selectedId || null}
              onInputChange={handleInputChange}
              onSelectionChange={handleSelectionChange}
              size="sm"
              variant="bordered"
              radius="md"
              isLoading={isLoadingFighters}
              className="min-w-0"
            >
              {availableFighters.map((item) => {
                const fullName = getFullName(item);
                const subtitle = getFighterSubtitle(item);

                return (
                  <AutocompleteItem key={String(item.id)} textValue={fullName}>
                    <div className="flex items-center gap-2.5 py-0.5">
                      <Avatar
                        src={getImageUrl(item.profileImage)}
                        name={fullName}
                        size="sm"
                        className="w-6 h-6 text-xs flex-shrink-0 cursor-pointer"
                        onClick={(event) => {
                          event.stopPropagation();
                          openFighterProfile(item.id);
                        }}
                      />

                      <div className="flex flex-col min-w-0">
                        <span
                          className="text-sm font-semibold text-gray-800 truncate cursor-pointer hover:text-blue-600 font-['Plus_Jakarta_Sans',sans-serif]"
                          onClick={(event) => {
                            event.stopPropagation();
                            openFighterProfile(item.id);
                          }}
                        >
                          {fullName}
                        </span>

                        {subtitle && (
                          <span className="text-[10px] text-gray-400 truncate">
                            {subtitle}
                          </span>
                        )}
                      </div>
                    </div>
                  </AutocompleteItem>
                );
              })}
            </AppAutocomplete>
          )}

          {isManualEntry && !isLocked && (
            <p className="mb-1 mt-1 text-[11px] leading-4 text-amber-600">
              {t(
                "fighter_not_in_system",
                "Not found in system — will be added as a manual entry."
              )}
            </p>
          )}

          {fieldError && (
            <p className="mb-3 mt-1 text-[11px] leading-4 text-red-500">
              {fieldError}
            </p>
          )}

          {fighter ? (
            <div className="rounded-lg bg-white border border-gray-200 p-3">
              <div className="flex items-center gap-3">
                <Avatar
                  src={getImageUrl(fighter.profileImage)}
                  name={getFullName(fighter)}
                  className={`w-14 h-14 flex-shrink-0 ${
                    isRed
                      ? "ring-2 ring-red-100"
                      : "ring-2 ring-blue-100"
                  }`}
                />

                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-800 truncate font-['Plus_Jakarta_Sans',sans-serif]">
                    {getFullName(fighter)}
                  </p>

                  <p className="text-[10px] text-gray-400 truncate">
                    {getNickname(fighter)}
                  </p>

                  <p className="text-[10px] text-gray-500 truncate mt-0.5">
                    {getFighterSubtitle(fighter)}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 mt-3">
                <div className="rounded-md bg-gray-50 px-2 py-1.5">
                  <p className="text-[10px] text-gray-400">
                    {t("nationality", "Nationality")}
                  </p>

                  <p className="text-[10px] font-medium text-gray-700 truncate">
                    {getNationality(fighter)}
                  </p>
                </div>

                <div className="rounded-md bg-gray-50 px-2 py-1.5">
                  <p className="text-[10px] text-gray-400">
                    {t("weight_class", "Weight Class")}
                  </p>

                  <p className="text-[10px] font-medium text-gray-700 truncate">
                    {getFighterWeightClass(fighter)}
                  </p>
                </div>

                <div className="rounded-md bg-gray-50 px-2 py-1.5">
                  <p className="text-[10px] text-gray-400">
                    {t("discipline", "Discipline")}
                  </p>

                  <p className="text-[10px] font-medium text-gray-700 truncate">
                    {getDiscipline(fighter)}
                  </p>
                </div>

                <div className="rounded-md bg-gray-50 px-2 py-1.5">
                  <p className="text-[10px] text-gray-400">
                    {t("stance", "Stance")}
                  </p>

                  <p className="text-[10px] font-medium text-gray-700 truncate">
                    {getStance(fighter)}
                  </p>
                </div>
              </div>

              {fighter.fighter?.titleHolder && (
                <div
                  className={`mt-2 rounded-md px-2.5 py-1.5 text-center ${
                    isRed
                      ? "bg-red-50"
                      : "bg-blue-50"
                  }`}
                >
                  <span
                    className={`text-[10px] font-semibold ${
                      isRed
                        ? "text-red-600"
                        : "text-blue-600"
                    }`}
                  >
                    {t("title_holder", "Title Holder")}
                  </span>
                </div>
              )}
            </div>
          ) : isManualEntry ? (
            <div className="rounded-lg bg-white border border-gray-200 p-3">
              <div className="flex items-center gap-3">
                <Avatar
                  name={typedName.trim()}
                  className={`w-14 h-14 flex-shrink-0 ${
                    isRed
                      ? "ring-2 ring-red-100"
                      : "ring-2 ring-blue-100"
                  }`}
                />

                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-800 truncate font-['Plus_Jakarta_Sans',sans-serif]">
                    {typedName.trim()}
                  </p>

                  <p className="text-[10px] text-gray-400 truncate">
                    {t("not_in_system", "Not registered on the platform")}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-[132px] rounded-lg border border-dashed border-gray-300 bg-white/70 flex flex-col items-center justify-center text-center px-3">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${
                  isRed
                    ? "bg-red-50"
                    : "bg-blue-50"
                }`}
              >
                <span
                  className={`text-lg font-semibold ${
                    isRed
                      ? "text-red-400"
                      : "text-blue-400"
                  }`}
                >
                  ?
                </span>
              </div>

              <p className="text-sm font-medium text-gray-500 font-['Plus_Jakarta_Sans',sans-serif]">
                {isRed
                  ? t("red_corner_fighter", "Red Corner Fighter")
                  : t("blue_corner_fighter", "Blue Corner Fighter")}
              </p>

              <p className="text-[10px] text-gray-400 mt-0.5">
                {t("select_or_type_fighter_above", "Select or type a fighter name above")}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="w-full sm:w-[720px] sm:max-w-[720px] h-full sm:h-auto bg-white sm:rounded-3xl shadow-[0px_14px_28px_0px_rgba(0,0,0,0.08)] flex flex-col overflow-hidden max-h-full sm:max-h-[90vh] font-['Plus_Jakarta_Sans',sans-serif]">
        <div className="flex items-center gap-3 px-4 sm:px-8 py-4 sm:py-5 border-b border-gray-100 shrink-0">
          <button
            type="button"
            onClick={handleClose}
            disabled={isLoading}
            className="sm:hidden size-9 rounded-full bg-gray-100 flex items-center justify-center text-gray-700 hover:bg-gray-200 transition-colors shrink-0"
          >
            <ArrowLeftIcon className="w-4 h-4" />
          </button>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900">
              {isEditMode
                ? t("edit_matchup", "Edit Matchup")
                : t("add_matchup", "Add Matchup")}
            </h2>
            <p className="text-sm font-normal text-gray-500 mt-0.5 hidden sm:block">
              {isEditMode
                ? t("edit_matchup_subtitle", "Update championship status or fighters")
                : t("add_matchup_subtitle", "Configure championship status and required fighters")}
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={isLoading}
            className="hidden sm:flex size-8 rounded-xl bg-gray-100 hover:bg-gray-200 items-center justify-center text-gray-500 transition-colors shrink-0"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 sm:px-8 py-5 space-y-5 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-start">
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700 block">
                {t("combat_sport", "Combat Sport")}{" "}
                <span className="text-blue-600">*</span>
              </label>
              <AppSelect
                aria-label="Combat Sport"
                placeholder={t("select_combat_sport", "Select combat sport")}
                selectedKeys={combatSport ? [combatSport] : []}
                onSelectionChange={(keys) => {
                  const selected = Array.from(keys)[0] as string;
                  setCombatSport(selected || "");
                  resetFighterSelection();
                  clearError("combatSport");
                }}
                size="sm"
                variant="bordered"
                radius="lg"
                isLoading={isLoadingOptions}
                className="min-w-0 w-full"
                classNames={{ value: "truncate" }}
              >
                {sports.map((sport) => (
                  <SelectItem key={sport} textValue={formatOption(sport)}>
                    {formatOption(sport)}
                  </SelectItem>
                ))}
              </AppSelect>
              {errors.combatSport && (
                <p className="text-[11px] leading-4 text-red-500">{errors.combatSport}</p>
              )}
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700 block">
                {t("weight_class", "Weight Class")}{" "}
                <span className="text-blue-600">*</span>
              </label>
              <AppSelect
                aria-label="Weight Class"
                placeholder={t("select_weight_class", "Select weight class")}
                selectedKeys={weightClass ? [weightClass] : []}
                onSelectionChange={(keys) => {
                  const selected = Array.from(keys)[0] as string;
                  setWeightClass(selected || "");
                  resetFighterSelection();
                  clearError("weightClass");
                }}
                size="sm"
                variant="bordered"
                radius="lg"
                isLoading={isLoadingOptions}
                className="min-w-0 w-full"
                classNames={{ value: "truncate" }}
              >
                {weightClasses.map((weight) => (
                  <SelectItem key={weight} textValue={formatOption(weight)}>
                    {formatOption(weight)}
                  </SelectItem>
                ))}
              </AppSelect>
              {errors.weightClass && (
                <p className="text-[11px] leading-4 text-red-500">{errors.weightClass}</p>
              )}
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700 block">
                {t("championship_title_fight", "Championship / Title Fight")}
              </label>
              <div className="h-[38px] w-full rounded-lg border border-gray-200 bg-white flex items-center justify-between px-3">
                <span className="text-xs font-medium text-gray-700 truncate pr-2">
                  {t("championship_title_fight", "Championship / Title Fight")}
                </span>
                <Checkbox
                  aria-label="Championship / Title Fight"
                  isSelected={titleFight}
                  onValueChange={setTitleFight}
                  color="primary"
                  size="sm"
                  className="flex-shrink-0"
                />
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="hidden sm:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white border border-gray-200 shadow-sm items-center justify-center">
              <span className="text-xs font-bold text-gray-400">{t("vs", "VS")}</span>
            </div>
            <div className="sm:hidden flex items-center justify-center my-2">
              <span className="text-sm font-bold text-gray-400 px-3 py-1 bg-gray-100 rounded-full">{t("vs", "VS")}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {renderFighterCard(redFighter, "red")}
              {renderFighterCard(blueFighter, "blue")}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-4 sm:px-8 py-4 border-t border-gray-100 shrink-0">
          <button
            type="button"
            onClick={handleClose}
            disabled={isLoading}
            className="hidden sm:flex px-5 py-2 rounded-full text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            {t("cancel", "Cancel")}
          </button>
          <Button
            color="primary"
            size="sm"
            className="flex-1 sm:flex-none px-5 py-2 rounded-full text-sm font-bold bg-blue-600 hover:bg-blue-700 text-white h-10 sm:h-auto"
            onPress={handleSubmit}
            isLoading={isLoading}
          >
            {isEditMode
              ? t("update", "Update")
              : t("add_matchup", "Add Matchup")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddMatchupModal;